import { atom, selector } from 'recoil'
import { UserList, UserListAddress } from 'src/common-services'
import { keyFactory } from 'src/utils/recoil'
import * as userListsAtoms from 'src/atoms/user-lists'
import { PlaceData } from '@googlemaps/google-maps-services-js'

type UserListsSection = 'user-lists' | 'user-list-detail' | UploadLocationsSection
type UploadLocationsSection = 'upload-csv' | 'match-data' | 'match-data-results' | 'upload-shapefile'
export type MatchedData = {
  address: string
  googlePlace: Partial<PlaceData> | undefined
}

const key = keyFactory('user-lists-modal')

export const open = atom<boolean>({
  key: key('open'),
  default: false,
})

export const currentSection = selector<UserListsSection>({
  key: key('current-section'),
  get: ({ get }) => {
    const _uploadSection = get(uploadSection)
    if (_uploadSection) {
      return _uploadSection
    }
    const selected = get(selectedList)
    if (selected) {
      return 'user-list-detail'
    }

    return 'user-lists'
  },
})

export const selectedListId = atom<string | null>({
  key: key('selected-list-id'),
  default: null,
})

export const listFilter = atom<string>({
  key: key('list-filter'),
  default: '',
})

export const selectedList = selector<UserList | undefined>({
  key: key('selected-list'),
  get: ({ get }) => {
    const listId = get(selectedListId)
    const lists = get(userListsAtoms.userLists)
    if (!lists) {
      return undefined
    }
    return lists.find((list) => list.id === listId)
  },
})

// upload csv atoms
export const uploadSection = atom<UploadLocationsSection | undefined>({
  key: key('upload-section'),
  default: undefined,
})

export const matchedData = atom<MatchedData[]>({
  key: key('matched-data'),
  default: [],
})

export const csvFile = atom<File | undefined>({
  key: key('csv-file'),
  default: undefined,
})

export const duplicatedMatchedData = selector<MatchedData[]>({
  key: key('duplicated-matched-data'),
  get: ({ get }) => {
    const data = get(matchedData)
    const list = get(selectedList)
    const duplicated = data.filter((item, index) => {
      return (
        item.googlePlace?.place_id &&
        (data.findIndex((i) => i.googlePlace?.place_id === item.googlePlace?.place_id) !== index ||
          list?.items?.some((x) => (x.data as UserListAddress).googlePlaceId === item.googlePlace?.place_id))
      )
    })
    return duplicated
  },
})

export const duplicatedMatchedDataQuantity = selector<number>({
  key: key('duplicated-matched-data-quantity'),
  get: ({ get }) => {
    const data = get(duplicatedMatchedData)
    return data.length
  },
})

export const uniqueMatchedData = selector<MatchedData[]>({
  key: key('unique-matched-data'),
  get: ({ get }) => {
    const data = get(matchedData)
    const matched = data.filter((item) => !!item.googlePlace)
    const list = get(selectedList)
    const unique = matched.filter((item, index) => {
      return (
        matched.findIndex((i) => i.googlePlace?.place_id === item.googlePlace?.place_id) === index &&
        !list?.items?.some((x) => (x.data as UserListAddress).googlePlaceId === item.googlePlace?.place_id)
      )
    })
    return unique
  },
})

export const matchedDataQuantity = selector<number>({
  key: key('matched-data-quantity'),
  get: ({ get }) => {
    const data = get(matchedData)
    const unmatched = get(unmatchedDataQuantity)
    const duplicated = get(duplicatedMatchedDataQuantity)

    return data.length - unmatched - duplicated
  },
})

export const unmatchedData = selector<MatchedData[]>({
  key: key('unmatched-data'),
  get: ({ get }) => {
    const data = get(matchedData)
    return data.filter((item) => !item.googlePlace)
  },
})

export const unmatchedDataQuantity = selector<number>({
  key: key('unmatched-data-quantity'),
  get: ({ get }) => {
    const data = get(unmatchedData)
    return data.length
  },
})

export const saveItemSuccessMessage = atom<string | undefined>({
  key: key('save-item-success-message'),
  default: undefined,
})
// upload csv atoms - end
